var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Api } from "../../Api";
import { DataFrame, Method, Status } from "../../DataFrame";
import { ControllerExtensionsService } from "../Controller/ControllerExtensionsService";
import { DevicesService } from "../Devices/DevicesService";
import { ResponseResult } from "../FieldChangeResult";
import { CannotGetRuntimeErrorCode, CannotGetRuntimeInfo, UpdatesProvider } from "./UpdatesProvider";
import { ControllerUpdatesInfo, ControllerUpdateType, ControllerVersion, DeviceUpdateShortInfo, UpdateHistory } from "./IUpdateInfo";
import { ProviderType, UpdateInfo, UpdateTypes, UpdatesProviderNotFound } from "./IUpdatesProvider";
import { BulkUpdateResult, BulkUpdateStatus, DownloadProgressInfo, DownloadTask, UpdateBranch, UpdateErrorCode, UpdateProgressInfo, UpdateState, UpdateStatus, UserInput } from './IUpdatesService';
import { AppState } from "../IAppStateService";
import { AppStateService } from "../AppStateService";
import { LoggerService } from "../Logging/LoggerService";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { Helpers, ParseObjToMap, Task } from "../../Helpers";
import { SessionService } from "../Session/SessionService";
import { ControllerConfigurationService } from "../Controller/ControllerConfigurationService";
export class UpdatesService {
    constructor() {
        this._controllerUpdates = [];
        this._updateRequestTimeout = 120000;
        this._oldUpdateVersion = 5;
        this._oldUpdateBuild = 56;
        this._updateOfflineStageTime = 15;
        this._updateProviders = Api.Get(UpdatesProvider.ServiceName);
        this._logger = Api.Get(LoggerService.ServiceName);
        this._connection = Api.Get(ExalusConnectionService.ServiceName);
        this._appStateService = Api.Get(AppStateService.ServiceName);
        this._extensionService = Api.Get(ControllerExtensionsService.ServiceName);
    }
    IsBranchAndAutomaticUpdatesSettingSupportedAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult)
                return false;
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
                return false;
            return true;
        });
    }
    DownloadControllerUpdateAsync(downloadProgress) {
        return __awaiter(this, void 0, void 0, function* () {
            // This method downloads the controller update using the new API, if available.
            // It does not install the update, only downloads it.
            try {
                const ver = yield this.getRuntimeInfoAsync();
                if (ver instanceof ResponseResult) {
                    return ver;
                }
                const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
                const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
                // Only supported for new API (version > _oldUpdateVersion/_oldUpdateBuild)
                if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                    return new ResponseResult(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot download update, feature unsupported in current version. Current version: ${ver}`);
                }
                // Use the API to download the update (downloadOnly = true)
                return yield this.DownloadControllerUpdateUsingApiAsync(() => { }, // No updateProgress callback needed for download-only
                downloadProgress, true);
            }
            catch (error) {
                return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Failed to download controller update: ${error}`);
            }
        });
    }
    InstallControllerOrRadioUpdateAsync(updateProgress, downloadProgress, updateGuid) {
        return __awaiter(this, void 0, void 0, function* () {
            const regGuid = new RegExp("^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$");
            const numberOfUpdates = this._controllerUpdates.length;
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult) {
                return ver;
            }
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            //Check if any updates are avaliable
            if (this._controllerUpdates.length == 0)
                return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot find any updates, make sure you check for available updates by calling CheckControllerUpdateAsync().`);
            //Install single update
            if (updateGuid !== undefined && (typeof updateGuid === 'string') && regGuid.test(updateGuid)) {
                let updateResult;
                const updateToInstall = this._controllerUpdates.find(update => update.UpdateIdentifier == updateGuid);
                if (updateToInstall == null)
                    return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Update with requested guid (${updateGuid}) not found.`);
                this._logger.Debug(`Installing single update type: ${updateToInstall.Update.UpdateType}, resource: ${updateToInstall.UpdatedResourceName}, version: ${updateToInstall.Update.Version}`);
                this._appStateService.DisallowHibernation();
                switch (updateToInstall.UpdateType) {
                    case ControllerUpdateType.RadioUpdate:
                        updateResult = yield (yield this._updateProviders.GetUpdatesProviderAsync(updateToInstall.ProviderName)).InstallRadioUpdateAsync(updateToInstall, updateProgress);
                        this._appStateService.AllowHibernation();
                        this._logger.Debug(`Radio update result: ${updateResult}`);
                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updateGuid);
                        return updateResult;
                    case ControllerUpdateType.SoftwareUpdate:
                        //Old way
                        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                            updateResult = yield this.UpdateControllerUsingScriptAsync(updateToInstall.Update, updateProgress);
                            this._appStateService.AllowHibernation();
                            if (updateResult == Status.OK)
                                this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updateGuid);
                        }
                        //New way
                        else {
                            let availableUpdate = yield this.CheckControllerUpdatesAsync();
                            //let isDownloaded = availableUpdate.UpdatesAvailable.any(a => a.IsDownloadOnly);
                            //if (!isDownloaded) {
                            //    await this.DownloadControllerUpdateAsync(downloadProgress);
                            //}
                            updateResult = yield this.UpdateControllerUsingApiAsync(updateProgress, downloadProgress, false);
                            this._appStateService.AllowHibernation();
                            if (updateResult == Status.OK)
                                this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updateGuid);
                        }
                        return updateResult;
                    default:
                        return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update with type ${updateToInstall.UpdateType} is not supported by UpdateService.`);
                }
            }
            //Install all updates
            else {
                //Handle update progress with multiple updates.
                const updateNumberRef = [1];
                const updateProgressWrapper = (progressInfo) => {
                    progressInfo.UpdateNumber = updateNumberRef[0];
                    progressInfo.AvailableUpdates = numberOfUpdates;
                    updateProgress(progressInfo);
                };
                const timeout = (ms) => __awaiter(this, void 0, void 0, function* () {
                    return new Promise(resolve => setTimeout(resolve, ms));
                });
                //First insall radio updates
                let updatesToInstall = this._controllerUpdates.where(update => update.UpdateType == ControllerUpdateType.RadioUpdate).toArray();
                let result = [];
                this._appStateService.DisallowHibernation();
                for (let i = 0; i < updatesToInstall.length; i++) {
                    const updateResult = yield (yield this._updateProviders.GetUpdatesProviderAsync(updatesToInstall[i].ProviderName)).InstallRadioUpdateAsync(updatesToInstall[i], updateProgressWrapper);
                    //Wait between updates
                    yield timeout(5000);
                    if (updateResult == Status.OK)
                        this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updatesToInstall[i].UpdateIdentifier);
                    result.push(updateResult);
                    updateNumberRef[0]++;
                }
                //Install other updates (in the future)
                //....//
                //
                //Install controller update
                const controlerUpdate = this._controllerUpdates.find(update => update.UpdateType == ControllerUpdateType.SoftwareUpdate);
                if (controlerUpdate != null) {
                    //Old way
                    if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                        const updateResult = yield this.UpdateControllerUsingScriptAsync(controlerUpdate.Update, updateProgressWrapper);
                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != controlerUpdate.UpdateIdentifier);
                        result.push(updateResult);
                        updateNumberRef[0]++;
                    }
                    //New way
                    else {
                        const updateResult = yield this.UpdateControllerUsingApiAsync(updateProgressWrapper, downloadProgress, false);
                        this._appStateService.AllowHibernation();
                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updateGuid);
                        result.push(updateResult);
                        updateNumberRef[0]++;
                    }
                }
                const resultWithError = result.where(r => r instanceof ResponseResult).toArray();
                if (resultWithError != null && resultWithError.length != 0) {
                    let errors = "";
                    resultWithError.forEach(err => errors += `${err.Type}, `);
                    this._appStateService.AllowHibernation();
                    if (resultWithError.any(err => err.Type == UpdateErrorCode.UpdateDownloadFailed))
                        return new ResponseResult(UpdateErrorCode.UpdateDownloadFailed, `Update download failed - not all updates were successfully downloaded. Installation error codes: ${errors}`);
                    return new ResponseResult(UpdateErrorCode.UpdatesPartiallyInstalled, `Update installation finished - not all updates were successfully installed. Installation error codes: ${errors}`);
                }
                this._appStateService.AllowHibernation();
                return Status.OK;
            }
        });
    }
    GetAutomaticControllerUpdateDownloadStatusAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult) {
                return ver;
            }
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
                return new ResponseResult(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot get update branch, feature unsupported in current version. Current version: ${ver}`);
            let result = yield this._connection.SendAndWaitForResponseAsync(new GetAutomaticUpdatesStatusRequest(), 10000, false);
            if (result.Status == Status.OK && result.Data)
                return true;
            else
                return false;
        });
    }
    GetServiceName() {
        return UpdatesService.ServiceName;
    }
    CheckDeviceVersionAsync(dev) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let device;
            if (typeof dev == 'string') {
                const result = yield Api.Get(DevicesService.ServiceName).GetDevice(dev);
                if (result != null)
                    device = result;
                else
                    return new ResponseResult(UpdateErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exists.`);
            }
            else {
                device = dev;
            }
            if (device.ProtocolGuid == null)
                return new ResponseResult(UpdateErrorCode.UnsupportedDevice, `Device does not contains data about protocol GUID.`);
            const softwareInfo = yield ((_a = (yield this._updateProviders.GetUpdatesProvidersByProtocolAsync(device.ProtocolGuid, ProviderType.Device)).first()) === null || _a === void 0 ? void 0 : _a.GetSoftwareVersionAsync(device));
            if (softwareInfo.Type != null)
                return softwareInfo;
            return softwareInfo;
        });
    }
    CheckDeviceUpdateAsync(dev) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let device;
            if (typeof dev == 'string') {
                const result = yield Api.Get(DevicesService.ServiceName).GetDevice(dev);
                if (result != null)
                    device = result;
                else
                    return new ResponseResult(UpdateErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exists.`);
            }
            else {
                device = dev;
            }
            if (device.ProtocolGuid == null)
                return new ResponseResult(UpdateErrorCode.UnsupportedDevice, `Device does not contains data about protocol GUID.`);
            const updateInfo = yield ((_a = (yield this._updateProviders.GetUpdatesProvidersByProtocolAsync(device.ProtocolGuid, ProviderType.Device)).first()) === null || _a === void 0 ? void 0 : _a.GetAvailableUpdateAsync(device));
            if (updateInfo.Type != null)
                return updateInfo;
            const result = new DeviceUpdate();
            result.CurrentResourceVersion = updateInfo.CurrentResourceVersion;
            result.NewResourceVersion = updateInfo.Update.Version;
            result.UpdatedResourceName = updateInfo.UpdatedResourceName;
            result.UpdateResourceGuid = updateInfo.Update.ResourceGuid;
            result.UpdateDescription = new Map([["pl-PL", updateInfo.Update.Description]]);
            let protocol = "n/a";
            try {
                protocol = (yield this._extensionService.GetProtocolInfoAsync(device.ProtocolGuid)).first().Name;
            }
            catch (error) {
                this._logger.Warning(`Cannot get protocol name! error: ${error}`);
            }
            result.Protocol = protocol;
            result.ProtocolGuid = device.ProtocolGuid;
            if ((yield Api.Get(DevicesService.ServiceName).GetDevicesAsync()).any(dev => dev.ModelGuid == "952ddc14-76c4-4f47-b0bc-5d106c1f9b02"))
                result.IsUpdateSafe = false;
            return result;
        });
    }
    CheckDeviceUpdatesBulkAsync(reloadCache) {
        return __awaiter(this, void 0, void 0, function* () {
            const updateProviders = yield (yield this._updateProviders.GetUpdatesProvidersAsync(ProviderType.Device));
            const result = [];
            for (var provider of updateProviders) {
                const updateInfo = yield provider.CheckUpdateBulkAsync(reloadCache);
                if (updateInfo instanceof ResponseResult)
                    return updateInfo;
                updateInfo.map((d) => __awaiter(this, void 0, void 0, function* () {
                    const update = new DeviceUpdate();
                    update.CurrentResourceVersion = d.CurrentResourceVersion;
                    update.NewResourceVersion = d.Update.Version;
                    update.UpdatedResourceName = d.UpdatedResourceName;
                    update.UpdateDescription = d.UpdateDescription;
                    update.IsUpdateSafe = true;
                    let protocol = "n/a";
                    try {
                        protocol = (yield this._extensionService.GetProtocolInfoAsync(provider.ProtocolGuid)).first().Name;
                    }
                    catch (error) {
                        this._logger.Warning(`Cannot get protocol name! error: ${error}`);
                    }
                    update.Protocol = protocol;
                    update.ProtocolGuid = provider.ProtocolGuid;
                    update.UpdateResourceGuid = d.Update.ResourceGuid;
                    result.push(update);
                }));
            }
            return result;
        });
    }
    InstallDeviceUpdateAsync(dev, updateProgress, updateAction, force = false) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let device;
            if (typeof dev == 'string') {
                const result = yield Api.Get(DevicesService.ServiceName).GetDevice(dev);
                if (result != null)
                    device = result;
                else
                    return new ResponseResult(UpdateErrorCode.CannotFindDevice, `Device with specified GUID ${dev} does not exists.`);
            }
            else {
                device = dev;
            }
            if (device.ProtocolGuid == null)
                return new ResponseResult(UpdateErrorCode.UnsupportedDevice, `Device does not contains data about protocol GUID.`);
            const updateInfo = yield ((_a = (yield this._updateProviders.GetUpdatesProvidersByProtocolAsync(device.ProtocolGuid, ProviderType.Device)).first()) === null || _a === void 0 ? void 0 : _a.GetAvailableUpdateAsync(device));
            if (updateInfo.Type != null)
                return updateInfo;
            this._appStateService.DisallowHibernation();
            const updateInstallResult = yield (yield this._updateProviders.GetUpdatesProvidersByProtocolAsync(device.ProtocolGuid, ProviderType.Device)).first().InstallUpdateAsync(updateInfo, updateProgress, updateAction, force);
            if (updateInstallResult.Type != null) {
                this._appStateService.AllowHibernation();
                return updateInstallResult;
            }
            this._appStateService.AllowHibernation();
            return Status.OK;
        });
    }
    InstallDevicesUpdateBulkAsync(deviceGuids, updateProgress, updateAction) {
        return __awaiter(this, void 0, void 0, function* () {
            const grouppedDevs = (yield Api.Get(DevicesService.ServiceName).GetDevicesAsync()).where(d => deviceGuids.includes(d.Guid)).groupBy(d => d.ProtocolGuid);
            const result = [];
            this._appStateService.DisallowHibernation();
            const grpCnt = grouppedDevs.count();
            for (let i = 0; i < grpCnt; i++) {
                const grp = grouppedDevs.elementAt(i);
                const totalStartRange = i * (100 / grpCnt);
                const totalEndRange = (i + 1) * (100 / grpCnt);
                const progressFunctionWrapper = (progress) => {
                    progress.PercentageTotal = Math.floor((progress.PercentageTotal / 100) * (totalEndRange - totalStartRange) + totalStartRange);
                    updateProgress(progress);
                };
                const updateProvider = yield (yield this._updateProviders.GetUpdatesProvidersByProtocolAsync(grp.key, ProviderType.Device));
                const r = yield updateProvider.first().InstalUpdateBulkAsync(grp.select(d => d.Guid).toArray(), progressFunctionWrapper, updateAction);
                if (r instanceof ResponseResult)
                    return r;
                result.push(r);
            }
            this._appStateService.AllowHibernation();
            if (result.length == 1)
                return result[0];
            else {
                const newBulkResult = new BulkUpdateResult();
                if (result.some(r => r.DevicesUpdateResults.count() > 0) && result.any(r => r.BulkUpdateStatus != BulkUpdateStatus.Success)) {
                    newBulkResult.BulkUpdateStatus = BulkUpdateStatus.PartialSuccess;
                }
                else if (result.any(r => r.BulkUpdateStatus != BulkUpdateStatus.Success)) {
                    newBulkResult.BulkUpdateStatus = result.first().BulkUpdateStatus;
                }
                else {
                    newBulkResult.BulkUpdateStatus = BulkUpdateStatus.Success;
                }
                for (const r of result) {
                    r.DevicesUpdateResults.forEach((value, key) => {
                        newBulkResult.DevicesUpdateResults.set(key, value);
                    });
                }
                return newBulkResult;
            }
        });
    }
    CheckControllerSoftwareUpdateAvailabilityAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = Api.Get(SessionService.ServiceName).User;
            if (user == null)
                return false;
            //Very old software, does not contains information about software version (before 15.02.2024)
            if (user.SoftwareVersion == "" || user.SoftwareVersion == null)
                return true;
            //Old software, containse information about software version but does not supports API that gets updates in backgroud (new way)
            const [major, build] = user.SoftwareVersion.split('.');
            if (parseFloat(major) < this._oldUpdateVersion || (parseFloat(major) == this._oldUpdateVersion && parseFloat(build) <= this._oldUpdateBuild))
                return true;
            const result = yield this._connection.SendAndWaitForResponseAsync(new PendingUpdatesRequest(), 10000, false);
            if (result == null || result.Status != Status.OK || result.Data == null)
                return false;
            const [majorPening, buildPending] = result.Data.Update.Version.ContainerSoftwareVersion.toString().split('.');
            if (parseInt(majorPening) > parseInt(major) || (parseInt(majorPening) == parseInt(major) && parseInt(buildPending) > parseInt(build)))
                return true;
            else
                return false;
        });
    }
    CanCancelFailedControllerUpdateOrGetPendingUpdateProgress() {
        return Api.Get(ControllerConfigurationService.ServiceName).CheckIfControllerSoftwareVersionIsEnough(6, 66);
    }
    CancelFailedControllerUpdateAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this._connection.SendAndWaitForResponseAsync(new CancelFailedUpdateRequest(), 10000, false);
            switch (result.Status) {
                case Status.OK:
                    return Status.OK;
                case Status.OperationNotPermitted:
                    return Status.OperationNotPermitted;
                case Status.Error:
                default:
                    return Status.Error;
            }
        });
    }
    CheckControllerPendingUpdateProgressAsync(updateProgress, downloadProgess) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const user = Api.Get(SessionService.ServiceName).User;
                if (user == null)
                    return new ResponseResult(UpdateErrorCode.UpdateActionsNotSupported, "No user data found, cannot check pending update progress.");
                //Very old software, does not contains information about software version (before 15.02.2024)
                if (user.SoftwareVersion == "" || user.SoftwareVersion == null)
                    return new ResponseResult(UpdateErrorCode.CannotGetCurrentControllerVersion, "No software version data found, cannot check pending update progress.");
                //Old software, containse information about software version but does not supports API that gets updates in backgroud (new way)
                const [major, build] = user.SoftwareVersion.split('.');
                if (parseFloat(major) < this._oldUpdateVersion || (parseFloat(major) == this._oldUpdateVersion && parseFloat(build) <= this._oldUpdateBuild))
                    return new ResponseResult(UpdateErrorCode.UnsupportedApiVersion, "Not supported API version, cannot check pending update progress.");
                const pendingUpdateResult = yield this._connection.SendAndWaitForResponseAsync(new PendingUpdatesRequest(), 10000, false);
                let isAnyUpdatePending = pendingUpdateResult.Status == Status.OK;
                if (!isAnyUpdatePending)
                    return new ResponseResult(UpdateErrorCode.CannotGetUpdates, "No pending updates found.");
                let downloadOnly = (_a = pendingUpdateResult.Data) === null || _a === void 0 ? void 0 : _a.DownloadOnly;
                let endPercentage = 100;
                const totalTime = this._updateOfflineStageTime * 60 * 1000; //  minutes
                let percentage = 0;
                const info = new UpdateProgressInfo();
                let downloadProgressEndPercentage = 50;
                let dp = new DownloadProgressInfo();
                let up = new UpdateProgressInfo();
                let receivedFirstFrame = false;
                if (!downloadOnly) {
                    dp.DownloadOnly = downloadOnly;
                    downloadProgressEndPercentage = 100;
                }
                let downloadProgressOnePercentageMultiplier = 100 / downloadProgressEndPercentage;
                const handleReponse = (data) => {
                    let tmp = data.Data;
                    this._logger.Debug(`Update progress: ${tmp.UpdateStep}, ${tmp.StepProgressInPercentage}%, ${tmp.UpdateProgressInPercentage}% data frame status: ${data.Status}`);
                    info.Percentage = Math.round(tmp.StepProgressInPercentage);
                    info.PercentageTotal = Math.round(tmp.UpdateProgressInPercentage);
                    dp.SpeedMbps = tmp.DownloadSpeedInMbs;
                    dp.Percentage = Math.round(tmp.StepProgressInPercentage);
                    dp.PercentageTotal = Math.round(tmp.UpdateProgressInPercentage);
                    dp.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                    up.AvailableUpdates = 1;
                    up.Percentage = tmp.StepProgressInPercentage;
                    up.PercentageTotal = tmp.UpdateProgressInPercentage;
                    up.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                    percentage = tmp.StepProgressInPercentage;
                    switch (tmp.UpdateStep) {
                        case UpdateStep.DownloadingInstaller:
                            dp.CurrentTask = DownloadTask.DownloadingInstaller;
                            up.Status = UpdateStatus.DownloadingPackage;
                            downloadProgess(dp);
                            //updateProgress(up);
                            break;
                        case UpdateStep.DownloadingUpdate:
                            dp.CurrentTask = DownloadTask.DownloadingUpdate;
                            up.Status = UpdateStatus.DownloadingPackage;
                            downloadProgess(dp);
                            //updateProgress(up);
                            break;
                        case UpdateStep.CheckingUpdate:
                            dp.CurrentTask = DownloadTask.CheckingUpdate;
                            up.Status = UpdateStatus.CheckingPackage;
                            //downloadProgess(dp);
                            updateProgress(up);
                            break;
                        case UpdateStep.PreparingUpdate:
                            up.Status = UpdateStatus.PreparingForUpgrade;
                            dp.CurrentTask = DownloadTask.CheckingUpdate;
                            updateProgress(up);
                            break;
                        case UpdateStep.OfflineUpdateStep:
                        case UpdateStep.InstallingUpdate:
                            up.Status = UpdateStatus.Upgrading;
                            updateProgress(up);
                            break;
                        case UpdateStep.InstallerFileSignCheckFailed:
                            up.Status = UpdateStatus.InstallationError;
                            updateProgress(up);
                            break;
                        case UpdateStep.UpdateFileSignCheckFailed:
                            up.Status = UpdateStatus.InstallationError;
                            updateProgress(up);
                            break;
                        case UpdateStep.UpdateDownloadFailed:
                            up.Status = UpdateStatus.InstallationStopped;
                            updateProgress(up);
                            break;
                        case UpdateStep.UpdateInstallationFailed:
                            up.Status = UpdateStatus.InstallationError;
                            updateProgress(up);
                            break;
                        case UpdateStep.UpdateFileCheckFailed:
                            up.Status = UpdateStatus.IncompatiblePackage;
                            updateProgress(up);
                            break;
                        case UpdateStep.UpdateDownloaded:
                            dp.Percentage = 100;
                            if (downloadOnly)
                                dp.PercentageTotal = Math.round(tmp.UpdateProgressInPercentage * downloadProgressOnePercentageMultiplier);
                            else
                                dp.PercentageTotal = Math.round(tmp.UpdateProgressInPercentage);
                            dp.SpeedMbps = 0;
                            dp.CurrentTask = DownloadTask.CheckingUpdate;
                            downloadProgess(dp);
                            dp.CurrentTask = DownloadTask.UpdateDownloadedAndChecked;
                            downloadProgess(dp);
                            break;
                    }
                };
                let intervalId = null;
                const onReconnect = (state) => __awaiter(this, void 0, void 0, function* () {
                    if (state == AppState.Connected) {
                        clearInterval(intervalId);
                        info.Status = UpdateStatus.Upgraded;
                        info.Percentage = 100;
                        info.PercentageTotal = 100;
                        updateProgress(info);
                        clearInterval(intervalId);
                        yield Task.Delay(5000);
                        resolve(Status.OK);
                        Api.Get(AppStateService.ServiceName).OnAppStateChanged().Unsubscribe(onReconnect);
                    }
                });
                const result = yield this._connection.SendAndHandleResponseAsync(new PendingUpdateProgressRequest(), 100000, (data) => {
                    var _a, _b, _c, _d, _e;
                    switch (data.Status) {
                        case Status.MultiDataResponseStart:
                            if (!receivedFirstFrame) {
                                if (((_a = data.Data) === null || _a === void 0 ? void 0 : _a.UpdateStep) == UpdateStep.UpdateDownloaded)
                                    endPercentage = 100;
                                else
                                    endPercentage = 50; // 50% for download progress
                                receivedFirstFrame = true;
                            }
                            handleReponse(data);
                            break;
                        case Status.MultiDataResponse:
                            handleReponse(data);
                            break;
                        case Status.MultiDataResponseStop:
                            switch ((_b = data.Data) === null || _b === void 0 ? void 0 : _b.UpdateStep) {
                                case UpdateStep.UpdateInstallationFailed:
                                    up.Status = UpdateStatus.InstallationError;
                                    updateProgress(up);
                                    info.Percentage = 0;
                                    info.PercentageTotal = 0;
                                    updateProgress(info);
                                    resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update installation failed!`));
                                    break;
                                case UpdateStep.InstallerFileSignCheckFailed:
                                case UpdateStep.UpdateFileSignCheckFailed:
                                case UpdateStep.UpdateDownloadFailed:
                                case UpdateStep.UpdateFileCheckFailed:
                                    up.Status = UpdateStatus.DownloadingFailure;
                                    updateProgress(up);
                                    info.Percentage = 0;
                                    info.PercentageTotal = 0;
                                    updateProgress(info);
                                    resolve(new ResponseResult(UpdateErrorCode.UpdateDownloadFailed, `Update download failed!`));
                                    break;
                                case UpdateStep.OfflineUpdateStep:
                                    handleReponse(data);
                                    info.Percentage = Math.round(data.Data.StepProgressInPercentage);
                                    info.PercentageTotal = Math.round(data.Data.UpdateProgressInPercentage);
                                    let oneUpdateProgressPercent = (100 - info.PercentageTotal) / 100;
                                    let updateProgressPercent = info.PercentageTotal;
                                    //info.PercentageTotal = resp.Data.UpdateProgressInPercentage as Progress; // 78%
                                    const incrementTime = totalTime / (endPercentage - percentage);
                                    intervalId = setInterval(() => {
                                        percentage++;
                                        updateProgressPercent += oneUpdateProgressPercent;
                                        if (updateProgressPercent > 100)
                                            updateProgressPercent = 100;
                                        if (percentage >= endPercentage) {
                                            info.Status = UpdateStatus.Upgraded;
                                            info.Percentage = 100;
                                            info.PercentageTotal = 100;
                                            updateProgress(info);
                                            clearInterval(intervalId);
                                            resolve(Status.OK);
                                        }
                                        else {
                                            info.Status = UpdateStatus.Upgrading;
                                            info.Percentage = Math.round(percentage);
                                            info.PercentageTotal = Math.round(updateProgressPercent);
                                            updateProgress(info);
                                        }
                                    }, incrementTime);
                                    Api.Get(AppStateService.ServiceName).OnAppStateChanged().Subscribe(onReconnect);
                                    break;
                                case UpdateStep.UpdateDownloaded:
                                    dp.Percentage = 100;
                                    if (downloadOnly)
                                        dp.PercentageTotal = Math.round(((_c = data.Data) === null || _c === void 0 ? void 0 : _c.UpdateProgressInPercentage) * downloadProgressOnePercentageMultiplier);
                                    else
                                        dp.PercentageTotal = Math.round((_d = data.Data) === null || _d === void 0 ? void 0 : _d.UpdateProgressInPercentage);
                                    dp.SpeedMbps = 0;
                                    dp.CurrentTask = DownloadTask.CheckingUpdate;
                                    downloadProgess(dp);
                                    dp.CurrentTask = DownloadTask.UpdateDownloadedAndChecked;
                                    downloadProgess(dp);
                                    if (downloadOnly)
                                        resolve(Status.OK);
                                    break;
                                default:
                                    handleReponse(data);
                                    resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update installation failed, received UpdateStep code: ${(_e = data.Data) === null || _e === void 0 ? void 0 : _e.UpdateStep}!`));
                                    break;
                            }
                            break;
                        default:
                            reject(new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot get pending update progress, response is empty or status is not OK. Status: ${data === null || data === void 0 ? void 0 : data.Status}`));
                    }
                }, true);
            }));
        });
    }
    CheckDevicesUpdatesAvailabilityAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = Api.Get(SessionService.ServiceName).User;
            if (user == null)
                return [];
            //Very old software, does not contains information about software version (before 15.02.2024)
            if (user.SoftwareVersion == "" || user.SoftwareVersion == null)
                return [];
            //Old software, containse information about software version but does not supports API that gets updates in backgroud (new way)
            const [major, build] = user.SoftwareVersion.split('.');
            if (parseFloat(major) < 6 || (parseFloat(major) == 6 && parseFloat(build) <= 4))
                return [];
            const result = yield this._connection.SendAndWaitForResponseAsync(new CheckDevicesUpdatesRequest(), 10000, false);
            const fResult = [];
            if (result == null || (result === null || result === void 0 ? void 0 : result.Status) != Status.OK || (result === null || result === void 0 ? void 0 : result.Data) == null)
                return [];
            result === null || result === void 0 ? void 0 : result.Data.forEach(d => {
                if (!d.IsAvailable)
                    return;
                const info = new DeviceUpdateShortInfo();
                info.DeviceGuid = d.DeviceGuid;
                info.CurrentVersion = d.CurrentVersion;
                info.NewVersion = d.NewVersion;
                fResult.push(info);
            });
            return fResult;
        });
    }
    CheckControllerUpdatesAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            this._controllerUpdates = [];
            let updateProviderServices = [];
            const updates = new ControllerUpdatesInfo();
            const runtimeInfo = yield this.getRuntimeInfoAsync();
            if (runtimeInfo instanceof ResponseResult) {
                const error = new ControllerUpdateNotAvailableInfo();
                error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                error.ResponseResult = runtimeInfo;
                updates.UpdatesNotAvailable.push(error);
                return updates;
            }
            const swVerInt = parseInt(runtimeInfo.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(runtimeInfo.SoftwareVersion.toString().split('.')[1]);
            try {
                updateProviderServices = yield this._updateProviders.GetUpdatesProvidersAsync(ProviderType.Controller);
            }
            catch (error) {
                if (error instanceof UpdatesProviderNotFound) {
                    this._logger.Warning(`Cannot get update providers for controller protocols! Avaliable only controller software updates!`);
                }
                else
                    throw error;
            }
            //Radio updates
            for (let i = 0; i < updateProviderServices.length; i++) {
                const result = yield updateProviderServices[i].GetAvailableRadioUpdateAsync();
                if (result.Type != null) {
                    const error = new ControllerUpdateNotAvailableInfo();
                    error.ResponseResult = result;
                    error.UpdateType = ControllerUpdateType.RadioUpdate;
                    let protocol = "n/a";
                    try {
                        protocol = (yield this._extensionService.GetProtocolInfoAsync(updateProviderServices[i].ProtocolGuid)).first().Name;
                    }
                    catch (error) {
                        this._logger.Warning(`Cannot get protocol name! error: ${error}`);
                    }
                    error.UpdatedResourceName = new Map([["pl-PL", protocol], ["en-US", protocol]]);
                    error.CurrentResourceVersion = yield updateProviderServices[i].GetCurrentRadioVersionAsync();
                    updates.UpdatesNotAvailable.push(error);
                }
                else {
                    const updateInfo = result;
                    const update = new ControllerUpdate();
                    update.UpdatedResourceName = new Map([["pl-PL", updateInfo.UpdatedResourceName], ["en-US", updateInfo.UpdatedResourceName]]);
                    update.CurrentResourceVersion = updateInfo.CurrentResourceVersion;
                    update.NewResourceVersion = updateInfo.Update.Version;
                    update.UpdateDescription = new Map([["pl-PL", updateInfo.Update.Description]]);
                    update.UpdateType = ControllerUpdateType.RadioUpdate;
                    update.UpdateIdentifier = Helpers.GenerateUUID();
                    update.UpdateIsDownloading = false;
                    update.DownloadProgress = 100;
                    update.UpdateCreationTime = null;
                    updates.UpdatesAvailable.push(update);
                    this._controllerUpdates.push(Object.assign(Object.assign({}, updateInfo), { UpdateIdentifier: update.UpdateIdentifier, ProviderName: updateProviderServices[i].GetUpdateProviderName(), UpdateType: ControllerUpdateType.RadioUpdate }));
                }
            }
            let isNewWayUpdate = true;
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
                isNewWayUpdate = false;
            //Software updates (new way)
            if (isNewWayUpdate) {
                const result = yield this._connection.SendAndWaitForResponseAsync(new PendingUpdatesRequest(), 15000, false);
                if (result == null || (result.Status != Status.OK && result.Status != Status.NoData))
                    throw (new Error(`Cannot get pending updates! Response is empty or status is not OK. Status: ${result === null || result === void 0 ? void 0 : result.Status}`));
                if (result.Data != null) {
                    const updateInfo = new UpdateInfo();
                    updateInfo.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                    updateInfo.UpdateDescription = ParseObjToMap(result.Data.Update.UpdateDescription);
                    updateInfo.UpdatedResourceName = ParseObjToMap(result.Data.Update.UpdateName).get("en-US");
                    const update = new ControllerUpdate();
                    update.UpdatedResourceName = ParseObjToMap(result.Data.Update.UpdateName);
                    update.UpdateDescription = ParseObjToMap(result.Data.Update.UpdateDescription);
                    update.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                    update.NewResourceVersion = `${result.Data.Update.Version.BaseRuntime}.${result.Data.Update.Version.ContainerSoftwareVersion}`;
                    update.UpdateType = ControllerUpdateType.SoftwareUpdate;
                    update.UpdateIdentifier = result.Data.Update.Guid;
                    update.UpdateIsDownloading = (result.Data.UpdateStep == UpdateStep.DownloadingInstaller
                        || result.Data.UpdateStep == UpdateStep.DownloadingUpdate
                        || result.Data.UpdateStep == UpdateStep.CheckingUpdate) ? true : false;
                    update.DownloadProgress = result.Data.DownloadProgress;
                    update.UpdateCreationTime = result.Data.Update.Creationtime;
                    update.IsDownloadOnly = result.Data.DownloadOnly;
                    updates.UpdatesAvailable.push(update);
                    this._controllerUpdates.push(Object.assign(Object.assign({}, updateInfo), { UpdateIdentifier: update.UpdateIdentifier, ProviderName: UpdatesService.ServiceName, UpdateType: ControllerUpdateType.SoftwareUpdate }));
                }
                else {
                    //If no updates - returns status NoData
                    const result = yield this._connection.SendAndWaitForResponseAsync(new CheckUpdatesRequest(), 15000, false);
                    if (result == null)
                        throw new Error(`Cannot check updates! Response is empty.`);
                    switch (result.Status) {
                        case Status.NoData:
                            const error = new ControllerUpdateNotAvailableInfo();
                            error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                            error.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                            error.ResponseResult = new ResponseResult(UpdateErrorCode.DeviceIsUpToDate, `Updates not found, controller is already up-to-date.`);
                            updates.UpdatesNotAvailable.push(error);
                            break;
                        case Status.OK:
                            if (result.Data == null)
                                throw new Error(`Cannot check updates! Response data is empty.`);
                            const updateInfo = new UpdateInfo();
                            updateInfo.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                            updateInfo.UpdateDescription = ParseObjToMap(result.Data.UpdateDescription);
                            updateInfo.UpdatedResourceName = ParseObjToMap(result.Data.UpdateName).get("en-US");
                            const update = new ControllerUpdate();
                            update.UpdatedResourceName = ParseObjToMap(result.Data.UpdateName);
                            update.UpdateDescription = ParseObjToMap(result.Data.UpdateDescription);
                            update.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                            update.UpdateType = ControllerUpdateType.SoftwareUpdate;
                            update.UpdateIdentifier = result.Data.Guid;
                            update.NewResourceVersion = `${result.Data.Version.BaseRuntime}.${result.Data.Version.ContainerSoftwareVersion}`;
                            update.UpdateIsDownloading = false;
                            update.DownloadProgress = 0;
                            update.UpdateCreationTime = result.Data.Creationtime;
                            updates.UpdatesAvailable.push(update);
                            this._controllerUpdates.push(Object.assign(Object.assign({}, updateInfo), { UpdateIdentifier: update.UpdateIdentifier, ProviderName: UpdatesService.ServiceName, UpdateType: ControllerUpdateType.SoftwareUpdate }));
                            break;
                        default:
                            throw new Error(`Cannot check updates! Response status is not OK. Status: ${result.Status}`);
                    }
                }
            }
            //Software updates (old way)
            else {
                const url = `https://exalus-updates.tr7.pl/software/${runtimeInfo.UpdateChannel}/${runtimeInfo.RuntimeVersion}/${runtimeInfo.SoftwareVersion}/update.json`;
                try {
                    const response = yield fetch(url);
                    let updateData;
                    let updateInfo = new UpdateInfo();
                    const error = new ControllerUpdateNotAvailableInfo();
                    error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                    error.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                    switch (response.status) {
                        case 200:
                            if (response.body != null)
                                updateData = (yield response.json());
                            break;
                        case 404:
                            error.ResponseResult = new ResponseResult(UpdateErrorCode.DeviceIsUpToDate, `Updates not found, controller is already up-to-date.`);
                            updates.UpdatesNotAvailable.push(error);
                            break;
                        default:
                            error.ResponseResult = new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Response status code does not indicate success. Status: ${response.status}`);
                            updates.UpdatesNotAvailable.push(error);
                            break;
                    }
                    if (updateData != null) {
                        updateInfo.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                        updateInfo.Update = updateData;
                        const update = new ControllerUpdate();
                        update.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                        update.CurrentResourceVersion = updateInfo.CurrentResourceVersion;
                        update.NewResourceVersion = updateInfo.Update.Version;
                        update.UpdateDescription = new Map([["pl-PL", updateInfo.Update.Description]]);
                        update.UpdateType = ControllerUpdateType.SoftwareUpdate;
                        update.UpdateIdentifier = Helpers.GenerateUUID();
                        update.DownloadProgress = 100;
                        update.UpdateIsDownloading = false;
                        update.UpdateCreationTime = null;
                        updates.UpdatesAvailable.push(update);
                        this._controllerUpdates.push(Object.assign(Object.assign({}, updateInfo), { UpdateIdentifier: update.UpdateIdentifier, ProviderName: UpdatesService.ServiceName, UpdateType: ControllerUpdateType.SoftwareUpdate }));
                    }
                    else if (response.status == 200)
                        throw (new Error(`Cannot get Update Data from response. Status: ${response.status}`));
                }
                catch (err) {
                    const runtimeInfo = (yield this.getRuntimeInfoAsync());
                    if (runtimeInfo instanceof ResponseResult) {
                        const error = new ControllerUpdateNotAvailableInfo();
                        error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                        error.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                        error.ResponseResult = runtimeInfo;
                        updates.UpdatesNotAvailable.push(error);
                    }
                    else {
                        const error = new ControllerUpdateNotAvailableInfo();
                        error.UpdateType = ControllerUpdateType.SoftwareUpdate;
                        error.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                        error.ResponseResult = new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot get available updates! ${err}`);
                        error.CurrentResourceVersion = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
                        updates.UpdatesNotAvailable.push(error);
                    }
                }
            }
            return updates;
        });
    }
    CheckControllerVersionAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const updateProviderServices = yield this._updateProviders.GetUpdatesProvidersAsync(ProviderType.Controller);
            const runtimeInfo = (yield this.getRuntimeInfoAsync());
            if (runtimeInfo instanceof ResponseResult) {
                return runtimeInfo;
            }
            const result = [];
            let version;
            //Software updates
            version = new ControllerVersion();
            version.Resource = "Controller software";
            version.Type = ControllerUpdateType.SoftwareUpdate;
            version.Version = `${runtimeInfo.RuntimeVersion}.${runtimeInfo.SoftwareVersion}`;
            result.push(version);
            //Radio updates
            for (let i = 0; i < updateProviderServices.length; i++) {
                version = new ControllerVersion();
                version.Version = yield updateProviderServices[i].GetCurrentRadioVersionAsync();
                version.Type = ControllerUpdateType.RadioUpdate;
                let protocol = "n/a";
                try {
                    protocol = (yield this._extensionService.GetProtocolInfoAsync(updateProviderServices[i].ProtocolGuid)).first().Name;
                }
                catch (error) {
                    this._logger.Warning(`Cannot get protocol name! error: ${error}`);
                }
                version.Resource = protocol;
                result.push(version);
            }
            return result;
        });
    }
    GetControllerUpdateBranchAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult) {
                return ver;
            }
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
                return new ResponseResult(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot get update branch, feature unsupported in current version. Current version: ${ver}`);
            const result = yield this._connection.SendAndWaitForResponseAsync(new GetUpdateBranchRequest(), 10000, false);
            if (result == null || (result === null || result === void 0 ? void 0 : result.Status) != Status.OK)
                return new ResponseResult(UpdateErrorCode.CannotGetUpdatesBranch, `Cannot get update branch, response is empty or status is not OK. Status: ${result === null || result === void 0 ? void 0 : result.Status}`);
            switch (result.Data) {
                case 0:
                    return UpdateBranch.Public;
                case 1:
                    return UpdateBranch.Beta;
                case 2:
                    return UpdateBranch.Development;
                default:
                    return new ResponseResult(UpdateErrorCode.CannotGetUpdatesBranch, `Cannot get update branch, unknown branch number. Branch number: ${result.Data}`);
            }
        });
    }
    SetControllerUpdateBranchAsync(updateBranch) {
        return __awaiter(this, void 0, void 0, function* () {
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult) {
                return ver;
            }
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
                return new ResponseResult(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot set update branch, feature unsupported in current version. Current version: ${ver}`);
            const result = yield this._connection.SendAndWaitForResponseAsync(new SetUpdateBranchRequest(updateBranch), 10000, false);
            if (result == null || (result === null || result === void 0 ? void 0 : result.Status) != Status.OK)
                return new ResponseResult(UpdateErrorCode.CannotSetUpdatesBranch, `Cannot set update branch, response is empty or status is not OK. Status: ${result === null || result === void 0 ? void 0 : result.Status}`);
            return Status.OK;
        });
    }
    EnableAutomaticControllerUpdateDownloadAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult) {
                return ver;
            }
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
                return new ResponseResult(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot enable automatic updates download, feature unsupported in current version. Current version: ${ver}`);
            const result = yield this._connection.SendAndWaitForResponseAsync(new EnableAutomaticUpdatesRequest(), 10000, false);
            if (result == null || (result === null || result === void 0 ? void 0 : result.Status) != Status.OK)
                return new ResponseResult(UpdateErrorCode.CannotSetUpdatesBranch, `Cannot enable automatic updates download, response is empty or status is not OK. Status: ${result === null || result === void 0 ? void 0 : result.Status}`);
            return Status.OK;
        });
    }
    DisableAutomaticControllerUpdateDownloadAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult) {
                return ver;
            }
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
                return new ResponseResult(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot disable automatic update download, feature unsupported in current version. Current version: ${ver}`);
            const result = yield this._connection.SendAndWaitForResponseAsync(new DisableAutomaticUpdatesRequest(), 10000, false);
            if (result == null || (result === null || result === void 0 ? void 0 : result.Status) != Status.OK)
                return new ResponseResult(UpdateErrorCode.CannotSetUpdatesBranch, `Cannot disable automatic update download, response is empty or status is not OK. Status: ${result === null || result === void 0 ? void 0 : result.Status}`);
            return Status.OK;
        });
    }
    GetControllerUpdatesHistoryAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult) {
                return ver;
            }
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild))
                return new ResponseResult(UpdateErrorCode.FeatureUnsupportedInCurrentVersion, `Cannot disable automatic update download, feature unsupported in current version. Current version: ${ver}`);
            const result = yield this._connection.SendAndWaitForResponseAsync(new GetUpdatesHistoryRequest(), 10000, false);
            if (result == null)
                return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot get updates history, response is empty.`);
            switch (result.Status) {
                case Status.OK:
                    if (result.Data != null)
                        return result.Data.map(d => {
                            const update = new UpdateHistory();
                            update.UpdatedResourceName = ParseObjToMap(d.UpdateName);
                            update.UpdateDescription = ParseObjToMap(d.UpdateDescription);
                            update.UpdateCreationTime = d.Creationtime;
                            update.UpdateChannel = d.UpdateChannel;
                            update.Version = `${d.Version.BaseRuntime}.${d.Version.ContainerSoftwareVersion}`;
                            return update;
                        });
                    else
                        return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot get updates history, response data is empty.`);
                case Status.NoData:
                    return [];
                default:
                    return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot get updates history, response status is not OK. Status: ${result.Status}`);
            }
        });
    }
    InstallControllerUpdateAsync(updateProgress, downloadProgress, guidOrAction, downloadOnlyOrAction, updateAction) {
        return __awaiter(this, void 0, void 0, function* () {
            const regGuid = new RegExp("^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$");
            const numberOfUpdates = this._controllerUpdates.length;
            const ver = (yield this.getRuntimeInfoAsync());
            if (ver instanceof ResponseResult) {
                return ver;
            }
            const swVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[0]);
            const buildVerInt = parseInt(ver.SoftwareVersion.toString().split('.')[1]);
            //Check if any updates are avaliable
            if (this._controllerUpdates.length == 0)
                return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Cannot find any updates, make sure you check for available updates by calling CheckControllerUpdateAsync().`);
            //Install single update
            if ((typeof guidOrAction === 'string') && regGuid.test(guidOrAction)) {
                let updateResult;
                const updateToInstall = this._controllerUpdates.find(update => update.UpdateIdentifier == guidOrAction);
                if (updateToInstall == null)
                    return new ResponseResult(UpdateErrorCode.CannotGetUpdates, `Update with requested guid (${guidOrAction}) not found.`);
                this._logger.Debug(`Installing single update type: ${updateToInstall.Update.UpdateType}, resource: ${updateToInstall.UpdatedResourceName}, version: ${updateToInstall.Update.Version}`);
                this._appStateService.DisallowHibernation();
                switch (updateToInstall.UpdateType) {
                    case ControllerUpdateType.RadioUpdate:
                        if (downloadOnlyOrAction)
                            return new ResponseResult(UpdateErrorCode.UpdateAlreadyDownloaded, `Update already downloaded set flag downloadOnly to false to install it.`);
                        updateResult = yield (yield this._updateProviders.GetUpdatesProviderAsync(updateToInstall.ProviderName)).InstallRadioUpdateAsync(updateToInstall, updateProgress, updateAction);
                        this._appStateService.AllowHibernation();
                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != guidOrAction);
                        return updateResult;
                    case ControllerUpdateType.SoftwareUpdate:
                        //Old way
                        if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                            if (downloadOnlyOrAction)
                                return new ResponseResult(UpdateErrorCode.UpdateAlreadyDownloaded, `Update already downloaded set flag downloadOnly to false to install it.`);
                            updateResult = yield this.UpdateControllerUsingScriptAsync(updateToInstall.Update, updateProgress);
                            this._appStateService.AllowHibernation();
                            if (updateResult == Status.OK)
                                this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != guidOrAction);
                        }
                        //New way
                        else {
                            let availableUpdate = yield this.CheckControllerUpdatesAsync();
                            let isDownloaded = availableUpdate.UpdatesAvailable.any(a => a.IsDownloadOnly);
                            updateResult = yield this.UpdateControllerUsingApiAsync(updateProgress, downloadProgress, isDownloaded ? false : downloadOnlyOrAction);
                            this._appStateService.AllowHibernation();
                            if (updateResult == Status.OK)
                                this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != guidOrAction);
                        }
                        return updateResult;
                    default:
                        return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update with type ${updateToInstall.UpdateType} is not supported by UpdateService.`);
                }
            }
            //Install all updates
            else {
                //Handle update progress with multiple updates.
                const updateNumberRef = [1];
                const updateProgressWrapper = (progressInfo) => {
                    progressInfo.UpdateNumber = updateNumberRef[0];
                    progressInfo.AvailableUpdates = numberOfUpdates;
                    updateProgress(progressInfo);
                };
                const timeout = (ms) => __awaiter(this, void 0, void 0, function* () {
                    return new Promise(resolve => setTimeout(resolve, ms));
                });
                //First insall radio updates
                let updatesToInstall = this._controllerUpdates.where(update => update.UpdateType == ControllerUpdateType.RadioUpdate).toArray();
                let result = [];
                this._appStateService.DisallowHibernation();
                for (let i = 0; i < updatesToInstall.length; i++) {
                    const updateResult = yield (yield this._updateProviders.GetUpdatesProviderAsync(updatesToInstall[i].ProviderName)).InstallRadioUpdateAsync(updatesToInstall[i], updateProgressWrapper, guidOrAction);
                    //Wait between updates
                    yield timeout(5000);
                    if (updateResult == Status.OK)
                        this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != updatesToInstall[i].UpdateIdentifier);
                    result.push(updateResult);
                    updateNumberRef[0]++;
                }
                //Install other updates (in the future)
                //....//
                //
                //Install controller update
                const controlerUpdate = this._controllerUpdates.find(update => update.UpdateType == ControllerUpdateType.SoftwareUpdate);
                if (controlerUpdate != null) {
                    //Old way
                    if (swVerInt < this._oldUpdateVersion || (swVerInt === this._oldUpdateVersion && buildVerInt <= this._oldUpdateBuild)) {
                        const updateResult = yield this.UpdateControllerUsingScriptAsync(controlerUpdate.Update, updateProgressWrapper);
                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != controlerUpdate.UpdateIdentifier);
                        result.push(updateResult);
                        updateNumberRef[0]++;
                    }
                    //New way
                    else {
                        const updateResult = yield this.UpdateControllerUsingApiAsync(updateProgressWrapper, downloadProgress, downloadOnlyOrAction);
                        this._appStateService.AllowHibernation();
                        if (updateResult == Status.OK)
                            this._controllerUpdates = this._controllerUpdates.filter(update => update.UpdateIdentifier != guidOrAction);
                        result.push(updateResult);
                        updateNumberRef[0]++;
                    }
                }
                const resultWithError = result.where(r => r instanceof ResponseResult).toArray();
                if (resultWithError != null && resultWithError.length != 0) {
                    let errors = "";
                    resultWithError.forEach(err => errors += `${err.Type}, `);
                    this._appStateService.AllowHibernation();
                    if (resultWithError.any(err => err.Type == UpdateErrorCode.UpdateDownloadFailed))
                        return new ResponseResult(UpdateErrorCode.UpdateDownloadFailed, `Update download failed - not all updates were successfully downloaded. Installation error codes: ${errors}`);
                    return new ResponseResult(UpdateErrorCode.UpdatesPartiallyInstalled, `Update installation finished - not all updates were successfully installed. Installation error codes: ${errors}`);
                }
                this._appStateService.AllowHibernation();
                return Status.OK;
            }
        });
    }
    UpdateControllerUsingScriptAsync(update, updateProgress) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            switch (update.UpdateType) {
                case UpdateTypes.Container:
                case UpdateTypes.ContainerSoftware:
                    try {
                        yield ((_a = this._connection) === null || _a === void 0 ? void 0 : _a.SendAndHandleResponseAsync(new ScriptControllerUpdateRequest(update.DownloadUri), this._updateRequestTimeout, (resp) => {
                            const progressInfo = new UpdateProgressInfo();
                            progressInfo.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                            switch (resp.Status) {
                                case Status.MultiDataResponseStart:
                                    progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                                    break;
                                case Status.MultiDataResponse:
                                    if (resp.Data == null) {
                                        this._logger.Warning(UpdatesService.ServiceName, `Recived update response without data! URI: ${update.DownloadUri}`);
                                        return;
                                    }
                                    progressInfo.Percentage = Math.round(resp.Data.Progress);
                                    switch (resp.Data.State) {
                                        case UpdateState.CheckingApplicability:
                                        case UpdateState.CheckingIntegrity:
                                        case UpdateState.CheckingPackageIntegrity:
                                            progressInfo.Status = UpdateStatus.CheckingPackage;
                                            break;
                                        case UpdateState.CreatingBackup:
                                        case UpdateState.MigratingData:
                                        case UpdateState.Removing:
                                            progressInfo.Status = UpdateStatus.MovingData;
                                            break;
                                        case UpdateState.Deploying:
                                        case UpdateState.Installing:
                                            progressInfo.Status = UpdateStatus.Upgrading;
                                            break;
                                        case UpdateState.Downloading:
                                            progressInfo.Status = UpdateStatus.DownloadingPackage;
                                            break;
                                        case UpdateState.Error:
                                            progressInfo.Status = UpdateStatus.Error;
                                            break;
                                        case UpdateState.Installed:
                                            progressInfo.Status = UpdateStatus.Upgraded;
                                            break;
                                        case UpdateState.PreparingSystem:
                                        case UpdateState.ReadyToDownload:
                                            progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                                            break;
                                        case UpdateState.UndoingInstallation:
                                            progressInfo.Status = UpdateStatus.InstallationStopped;
                                            break;
                                        case UpdateState.Unpackaging:
                                            progressInfo.Status = UpdateStatus.MovingData;
                                            break;
                                        case UpdateState.WaitingForUser:
                                            progressInfo.Status = UpdateStatus.Upgrading;
                                            break;
                                    }
                                    break;
                                case Status.MultiDataResponseStop:
                                    progressInfo.Status = UpdateStatus.OK;
                                    break;
                                case Status.Error:
                                case Status.FatalError:
                                    progressInfo.Status = UpdateStatus.Error;
                                    break;
                            }
                            updateProgress(progressInfo);
                        }, false));
                        return Status.OK;
                    }
                    catch (error) {
                        return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Cannot process update, error: ${error}`);
                    }
                case UpdateTypes.RuntimeFixingScript:
                    var howManyDevices = 10;
                    let extVersion = "n/a";
                    try {
                        extVersion = (yield this._extensionService.GetExtensionsInfoAsync()).first(ext => ext.ExtensionGuid == "f4f45dac-beda-441a-8d29-efc6dc977cd1").ExtensionVersion;
                    }
                    catch (error) {
                        this._logger.Warning(`Cannot get protocol name! error: ${error}`);
                    }
                    var updateExtVersion = extVersion;
                    const ver = (yield this.getRuntimeInfoAsync());
                    if (ver instanceof ResponseResult) {
                        return ver;
                    }
                    var isNewApi = parseFloat(ver.RuntimeVersion) >= 2.0 ? true : (parseFloat(updateExtVersion) >= 1.2201125);
                    try {
                        howManyDevices = (yield Api.Get(DevicesService.ServiceName).GetDevicesAsync()).length;
                    }
                    catch (error) {
                        this._logger.Warning(UpdatesService.ServiceName, `Cannot get number of devices in system! Error: ${error}. Using default value of 10 devices.`);
                    }
                    if (isNewApi) {
                        let intervalId;
                        let progressInfo = new UpdateProgressInfo();
                        progressInfo.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                        progressInfo.Percentage = 1;
                        progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                        updateProgress(progressInfo);
                        let i = 2;
                        const startTime = Date.now();
                        const howLongToUpgrade = (howManyDevices * 900) + 1600000;
                        const onePercentInTime = howLongToUpgrade / 100;
                        const willEndAt = startTime + howLongToUpgrade;
                        const updatePromise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                            var _g;
                            try {
                                const OnReturnedFromSuspensionHandler = () => {
                                    this._appStateService.OnReturnedFromSuspension().Unsubscribe(OnReturnedFromSuspensionHandler);
                                    if (Date.now() > willEndAt) {
                                        clearInterval(intervalId);
                                        this._logger.Debug(`Update already finished!`);
                                        progressInfo.Percentage = 100;
                                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                        progressInfo.Status = UpdateStatus.Upgraded;
                                        updateProgress(progressInfo);
                                        resolve(Status.OK);
                                    }
                                    else {
                                        var msToEnd = (willEndAt - Date.now());
                                        if (msToEnd < howLongToUpgrade) {
                                            i = (95 - (msToEnd / onePercentInTime));
                                        }
                                        else {
                                            clearInterval(intervalId);
                                            intervalId = setInterval(() => {
                                                clearInterval(intervalId);
                                                progressInfo.Percentage = 100;
                                                progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                                progressInfo.Status = UpdateStatus.Upgraded;
                                                updateProgress(progressInfo);
                                                resolve(Status.OK);
                                            }, msToEnd);
                                        }
                                    }
                                };
                                this._appStateService.OnReturnedFromSuspension().Subscribe(OnReturnedFromSuspensionHandler);
                                intervalId = setInterval(() => {
                                    if (i > 99) {
                                        clearInterval(intervalId);
                                        progressInfo.Percentage = 100;
                                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                        progressInfo.Status = UpdateStatus.Upgraded;
                                        updateProgress(progressInfo);
                                        resolve(Status.OK);
                                    }
                                    else {
                                        var percent = i;
                                        if (percent < 99) {
                                            progressInfo.Percentage = Math.round(percent);
                                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                            progressInfo.Status = UpdateStatus.Upgrading;
                                            updateProgress(progressInfo);
                                        }
                                    }
                                    i++;
                                }, onePercentInTime);
                                const result = yield ((_g = this._connection) === null || _g === void 0 ? void 0 : _g.SendAndWaitForResponseAsync(new InstallRequest(update.DownloadUri), this._updateRequestTimeout, false));
                                if (result.Status != Status.OK) {
                                    clearInterval(intervalId);
                                    progressInfo.Percentage = 0;
                                    progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                    progressInfo.Status = UpdateStatus.InstallationError;
                                    updateProgress(progressInfo);
                                    this._logger.Debug(UpdatesService.ServiceName, `Update failed with error. Update response status: ${result.Status}`);
                                    resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update failed with error. Update response status: ${result.Status}`));
                                }
                            }
                            catch (error) {
                                this._logger.Debug(UpdatesService.ServiceName, `Update failed with error. ${error}`);
                                progressInfo.Percentage = 0;
                                progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                progressInfo.Status = UpdateStatus.Error;
                                updateProgress(progressInfo);
                                clearInterval(intervalId);
                                resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update failed with error. ${error}`));
                            }
                        }));
                        return yield updatePromise;
                    }
                    else {
                        let progressInfo = new UpdateProgressInfo();
                        progressInfo.UpdatedResourceName = new Map([["pl-PL", "Oprogramowanie kontrolera"], ["en-US", "Controller software"]]);
                        progressInfo.Percentage = 1;
                        progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                        progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                        updateProgress(progressInfo);
                        const currentAuthData = this._connection.GetAuthorizationInfo();
                        if (currentAuthData == null)
                            return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Cannot process update. Cannot get current controller login credentials!`);
                        const result = yield ((_b = this._connection) === null || _b === void 0 ? void 0 : _b.SendAndWaitForResponseAsync(new LoginUserRequest({ Email: `support@support`, Password: `support@${currentAuthData.SerialNumber}:${currentAuthData.PIN}` }), 12000, false));
                        if (result.Status === Status.OK) {
                            progressInfo.Percentage = 1;
                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                            progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                            updateProgress(progressInfo);
                            let executeCommandResult = yield ((_c = this._connection) === null || _c === void 0 ? void 0 : _c.SendAndWaitForResponseAsync(new ExecuteCommandRequest("rm /runtime/tmp/update.sh"), 12000, false));
                            if (executeCommandResult.Status == Status.OK) {
                                this._logger.Debug(`Removed previous script`);
                                progressInfo.Percentage = 2;
                                progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                                updateProgress(progressInfo);
                                executeCommandResult = yield ((_d = this._connection) === null || _d === void 0 ? void 0 : _d.SendAndWaitForResponseAsync(new ExecuteCommandRequest(`wget ${update.DownloadUri} -O /runtime/tmp/update.sh --no-check-certificate`), this._updateRequestTimeout, false));
                                if (executeCommandResult.Status == Status.OK) {
                                    this._logger.Debug(`Downloaded update script`);
                                    progressInfo.Percentage = 5;
                                    progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                    progressInfo.Status = UpdateStatus.PreparingForUpgrade;
                                    updateProgress(progressInfo);
                                    try {
                                        executeCommandResult = yield ((_e = this._connection) === null || _e === void 0 ? void 0 : _e.SendAndWaitForResponseAsync(new ExecuteCommandRequest(`chmod a+x /runtime/tmp/update.sh`), 12000, false));
                                        if (executeCommandResult.Status == Status.OK) {
                                            this._logger.Debug(`Executed update script`);
                                            progressInfo.Percentage = 5;
                                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                            progressInfo.Status = UpdateStatus.Upgrading;
                                            updateProgress(progressInfo);
                                            let i = 0;
                                            let intervalId;
                                            const onePercent = 95 / 100.0;
                                            const startTime = Date.now();
                                            const howLongToUpgrade = (howManyDevices * 900) + 1500000; //ms
                                            const onePercentInTime = howLongToUpgrade / 95;
                                            const willEndAt = startTime + howLongToUpgrade;
                                            const updatePromise = new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                                                var _h;
                                                try {
                                                    const OnReturnedFromSuspensionHandler = () => {
                                                        this._appStateService.OnReturnedFromSuspension().Unsubscribe(OnReturnedFromSuspensionHandler);
                                                        if (Date.now() > willEndAt) {
                                                            clearInterval(intervalId);
                                                            this._logger.Debug(`Update already finished!`);
                                                            progressInfo.Percentage = 100;
                                                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                                            progressInfo.Status = UpdateStatus.Upgraded;
                                                            updateProgress(progressInfo);
                                                            resolve(Status.OK);
                                                        }
                                                        else {
                                                            var msToEnd = (willEndAt - Date.now());
                                                            if (msToEnd < howLongToUpgrade) {
                                                                i = (95 - (msToEnd / onePercentInTime));
                                                            }
                                                            else {
                                                                clearInterval(intervalId);
                                                                intervalId = setInterval(() => {
                                                                    clearInterval(intervalId);
                                                                    progressInfo.Percentage = 100;
                                                                    progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                                                    progressInfo.Status = UpdateStatus.Upgraded;
                                                                    updateProgress(progressInfo);
                                                                    resolve(Status.OK);
                                                                }, msToEnd);
                                                            }
                                                        }
                                                    };
                                                    this._appStateService.OnReturnedFromSuspension().Subscribe(OnReturnedFromSuspensionHandler);
                                                    intervalId = setInterval(() => {
                                                        if (i > 99) {
                                                            clearInterval(intervalId);
                                                            progressInfo.Percentage = 100;
                                                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                                            progressInfo.Status = UpdateStatus.Upgraded;
                                                            updateProgress(progressInfo);
                                                            resolve(Status.OK);
                                                        }
                                                        else {
                                                            var percent = (5 + (i * onePercent));
                                                            ;
                                                            if (percent < 99) {
                                                                progressInfo.Percentage = Math.round(percent);
                                                                progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                                                progressInfo.Status = UpdateStatus.Upgrading;
                                                                updateProgress(progressInfo);
                                                            }
                                                        }
                                                        i++;
                                                    }, onePercentInTime);
                                                    (_h = this._connection) === null || _h === void 0 ? void 0 : _h.SendAndWaitForResponseAsync(new ExecuteCommandRequest(`/runtime/tmp/update.sh`), 12000, false);
                                                }
                                                catch (error) {
                                                    resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update failed with error. ${error}`));
                                                }
                                            }));
                                            return yield updatePromise;
                                        }
                                        else {
                                            progressInfo.Percentage = 0;
                                            progressInfo.PercentageTotal = Math.round(progressInfo.Percentage);
                                            progressInfo.Status = UpdateStatus.InstallationError;
                                            updateProgress(progressInfo);
                                            return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Execution failed!`);
                                        }
                                    }
                                    catch (err) {
                                        (_f = this._connection) === null || _f === void 0 ? void 0 : _f.SendAndWaitForResponseAsync(new ExecuteCommandRequest(`/runtime/scripts/restart_all.sh`), 12000, false);
                                        return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Execution failed! Error ${err}`);
                                    }
                                }
                                return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Execution failed! Cannot clear old update script!`);
                            }
                        }
                        return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Execution failed! Cannot loggin using support account!`);
                    }
                default:
                    return new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Not supported update type, type: ${update.UpdateType}`);
            }
        });
    }
    DownloadControllerUpdateNewApiAsync(pendingUpdate, updateProgress, downloadProgess, downloadOnly) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                let downloadProgressEndPercentage = 50;
                if (!downloadOnly)
                    downloadProgressEndPercentage = 100;
                let downloadProgressOnePercentageMultiplier = 100 / downloadProgressEndPercentage;
                yield this._connection.SendAndHandleResponseAsync(new DownloadUpdateRequest(), this._updateRequestTimeout, (resp) => {
                    var _a, _b, _c, _d, _e, _f;
                    const info = new DownloadProgressInfo();
                    info.DownloadOnly = downloadOnly;
                    info.UpdatedResourceName = ParseObjToMap(pendingUpdate.Update.UpdateName);
                    switch (resp.Status) {
                        case Status.MultiDataResponseStart:
                            info.Percentage = 0;
                            info.SpeedMbps = 0;
                            info.PercentageTotal = 0;
                            info.CurrentTask = DownloadTask.DownloadingInstaller;
                            downloadProgess(info);
                            break;
                        case Status.MultiDataResponse:
                            if (resp.Data != null) {
                                info.Percentage = resp.Data.StepProgressInPercentage;
                                info.SpeedMbps = resp.Data.DownloadSpeedInMbs;
                                if (downloadOnly)
                                    info.PercentageTotal = Math.round(((_a = resp.Data) === null || _a === void 0 ? void 0 : _a.UpdateProgressInPercentage) * downloadProgressOnePercentageMultiplier);
                                else
                                    info.PercentageTotal = Math.round((_b = resp.Data) === null || _b === void 0 ? void 0 : _b.UpdateProgressInPercentage);
                                switch (resp.Data.UpdateStep) {
                                    case UpdateStep.DownloadingInstaller:
                                        info.CurrentTask = DownloadTask.DownloadingInstaller;
                                        break;
                                    case UpdateStep.DownloadingUpdate:
                                        info.CurrentTask = DownloadTask.DownloadingUpdate;
                                        break;
                                    case UpdateStep.UpdateDownloaded:
                                        info.Percentage = 100;
                                        if (downloadOnly)
                                            info.PercentageTotal = Math.round(((_c = resp.Data) === null || _c === void 0 ? void 0 : _c.UpdateProgressInPercentage) * downloadProgressOnePercentageMultiplier);
                                        else
                                            info.PercentageTotal = Math.round((_d = resp.Data) === null || _d === void 0 ? void 0 : _d.UpdateProgressInPercentage);
                                        info.SpeedMbps = 0;
                                        info.CurrentTask = DownloadTask.CheckingUpdate;
                                        downloadProgess(info);
                                        info.CurrentTask = DownloadTask.UpdateDownloadedAndChecked;
                                        downloadProgess(info);
                                        resolve(Status.OK);
                                        break;
                                    default:
                                        info.CurrentTask = DownloadTask.CheckingUpdate;
                                        break;
                                }
                                downloadProgess(info);
                            }
                            break;
                        case Status.MultiDataResponseStop:
                            if (resp.Data != null) {
                                switch (resp.Data.UpdateStep) {
                                    case UpdateStep.UpdateDownloaded:
                                        info.Percentage = 100;
                                        if (downloadOnly)
                                            info.PercentageTotal = Math.round(((_e = resp.Data) === null || _e === void 0 ? void 0 : _e.UpdateProgressInPercentage) * downloadProgressOnePercentageMultiplier);
                                        else
                                            info.PercentageTotal = Math.round((_f = resp.Data) === null || _f === void 0 ? void 0 : _f.UpdateProgressInPercentage);
                                        info.SpeedMbps = 0;
                                        info.CurrentTask = DownloadTask.CheckingUpdate;
                                        downloadProgess(info);
                                        info.CurrentTask = DownloadTask.UpdateDownloadedAndChecked;
                                        downloadProgess(info);
                                        resolve(Status.OK);
                                        break;
                                    case UpdateStep.UpdateDownloadFailed:
                                        info.Percentage = 0;
                                        info.PercentageTotal = 0;
                                        info.SpeedMbps = 0;
                                        info.CurrentTask = DownloadTask.Unknown;
                                        downloadProgess(info);
                                        resolve(new ResponseResult(UpdateErrorCode.UpdateDownloadFailed, `Update download failed!`));
                                }
                            }
                            break;
                        case Status.Error:
                        case Status.FatalError:
                            info.Percentage = 0;
                            info.PercentageTotal = 0;
                            info.SpeedMbps = 0;
                            info.CurrentTask = DownloadTask.Unknown;
                            downloadProgess(info);
                            resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update download failed!`));
                            break;
                    }
                }, false);
            }));
        });
    }
    InstallUpdateNewApiAsync(pendingUpdate, updateProgress, downloadProgress, downloadOnly) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                const endPercentage = 100;
                const totalTime = this._updateOfflineStageTime * 60 * 1000; // 9.3 minutes
                let percentage = 0;
                const info = new UpdateProgressInfo();
                let intervalId = null;
                pendingUpdate.DownloadOnly = false;
                const onReconnect = (state) => __awaiter(this, void 0, void 0, function* () {
                    if (state == AppState.Connected) {
                        clearInterval(intervalId);
                        info.Status = UpdateStatus.Upgraded;
                        info.Percentage = 100;
                        info.PercentageTotal = 100;
                        updateProgress(info);
                        clearInterval(intervalId);
                        yield Task.Delay(5000);
                        resolve(Status.OK);
                        Api.Get(AppStateService.ServiceName).OnAppStateChanged().Unsubscribe(onReconnect);
                    }
                });
                yield this._connection.SendAndHandleResponseAsync(new InstallUpdateRequest(), this._updateRequestTimeout, (resp) => {
                    var _a, _b;
                    info.UpdatedResourceName = ParseObjToMap(pendingUpdate.Update.UpdateName);
                    switch (resp.Status) {
                        case Status.MultiDataResponseStart:
                            info.Status = UpdateStatus.PreparingForUpgrade;
                            info.Percentage = (_a = resp.Data) === null || _a === void 0 ? void 0 : _a.StepProgressInPercentage;
                            info.PercentageTotal = (_b = resp.Data) === null || _b === void 0 ? void 0 : _b.UpdateProgressInPercentage;
                            updateProgress(info);
                            break;
                        case Status.MultiDataResponse:
                            if (resp.Data != null) {
                                info.Status = UpdateStatus.Upgrading;
                                info.Percentage = Math.round(resp.Data.StepProgressInPercentage);
                                info.PercentageTotal = Math.round(resp.Data.UpdateProgressInPercentage);
                                percentage = resp.Data.StepProgressInPercentage;
                                updateProgress(info);
                            }
                            break;
                        case Status.MultiDataResponseStop:
                            if (resp.Data != null) {
                                switch (resp.Data.UpdateStep) {
                                    case UpdateStep.UpdateInstallationFailed:
                                        info.Status = UpdateStatus.InstallationError;
                                        info.Percentage = 0;
                                        info.PercentageTotal = 0;
                                        updateProgress(info);
                                        resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update installation failed!`));
                                        break;
                                    //Add 5 minutes to finish offline stage
                                    case UpdateStep.OfflineUpdateStep:
                                        {
                                            info.Percentage = Math.round(resp.Data.StepProgressInPercentage);
                                            info.PercentageTotal = Math.round(resp.Data.UpdateProgressInPercentage);
                                            let oneUpdateProgressPercent = (100 - info.PercentageTotal) / 100;
                                            let updateProgressPercent = info.PercentageTotal;
                                            //info.PercentageTotal = resp.Data.UpdateProgressInPercentage as Progress; // 78%
                                            const incrementTime = totalTime / (endPercentage - percentage);
                                            intervalId = setInterval(() => {
                                                percentage++;
                                                updateProgressPercent += oneUpdateProgressPercent;
                                                if (updateProgressPercent > 100)
                                                    updateProgressPercent = 100;
                                                if (percentage >= endPercentage) {
                                                    info.Status = UpdateStatus.Upgraded;
                                                    info.Percentage = 100;
                                                    info.PercentageTotal = 100;
                                                    updateProgress(info);
                                                    clearInterval(intervalId);
                                                    resolve(Status.OK);
                                                }
                                                else {
                                                    info.Status = UpdateStatus.Upgrading;
                                                    info.Percentage = Math.round(percentage);
                                                    info.PercentageTotal = Math.round(updateProgressPercent);
                                                    updateProgress(info);
                                                }
                                            }, incrementTime);
                                            Api.Get(AppStateService.ServiceName).OnAppStateChanged().Subscribe(onReconnect);
                                        }
                                        break;
                                }
                            }
                            break;
                        case Status.Error:
                        case Status.FatalError:
                            info.Status = UpdateStatus.Error;
                            info.Percentage = 0;
                            info.PercentageTotal = 0;
                            updateProgress(info);
                            resolve(new ResponseResult(UpdateErrorCode.CannotProcessUpdate, `Update installation failed!`));
                            break;
                    }
                }, false);
            }));
        });
    }
    InstallControllerUpdateUsingApiAsync(updateProgress, downloadProgress) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this._connection.SendAndWaitForResponseAsync(new PendingUpdatesRequest(), 10000, false);
            if ((result === null || result === void 0 ? void 0 : result.Status) == Status.NoData)
                return new ResponseResult(UpdateErrorCode.DeviceIsUpToDate, `No updates available!`);
            if (result == null || (result === null || result === void 0 ? void 0 : result.Status) != Status.OK || (result === null || result === void 0 ? void 0 : result.Data) == null)
                throw new Error(`Cannot get pending updates! Response is empty or status is not OK. Status: ${result === null || result === void 0 ? void 0 : result.Status}`);
            switch (result.Data.UpdateStep) {
                case UpdateStep.DownloadingInstaller:
                case UpdateStep.DownloadingUpdate:
                    return new ResponseResult(UpdateErrorCode.UpdateAlreadyDownloaded, `Update already downloaded, or download in progress.`);
                case UpdateStep.InstallingUpdate:
                case UpdateStep.PreparingUpdate:
                    return new ResponseResult(UpdateErrorCode.UpdateAlreadyInstalled, `Update already installed, or installation in progress.`);
                case UpdateStep.UpdateDownloaded:
                    result.Data.DownloadOnly = false; // We are installing update, not downloading it
                    return yield this.InstallUpdateNewApiAsync(result.Data, updateProgress, downloadProgress, false);
                default:
                    this._logger.Debug(UpdatesService.ServiceName, `Installing update using new API. Update step: ${result.Data.UpdateStep} downloadOnly: false`);
                    result.Data.DownloadOnly = false; // We are installing update and downloading it
                    var downloadResult = yield this.DownloadControllerUpdateNewApiAsync(result.Data, updateProgress, downloadProgress, false);
                    if (downloadResult != Status.OK)
                        return downloadResult;
                    else
                        return yield this.InstallUpdateNewApiAsync(result.Data, updateProgress, downloadProgress, false);
            }
        });
    }
    DownloadControllerUpdateUsingApiAsync(updateProgress, downloadProgress, downloadOnly) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this._connection.SendAndWaitForResponseAsync(new PendingUpdatesRequest(), 10000, false);
            if ((result === null || result === void 0 ? void 0 : result.Status) == Status.NoData)
                return new ResponseResult(UpdateErrorCode.DeviceIsUpToDate, `No updates available!`);
            if (result == null || (result === null || result === void 0 ? void 0 : result.Status) != Status.OK || (result === null || result === void 0 ? void 0 : result.Data) == null)
                throw new Error(`Cannot get pending updates! Response is empty or status is not OK. Status: ${result === null || result === void 0 ? void 0 : result.Status}`);
            switch (result.Data.UpdateStep) {
                case UpdateStep.DownloadingInstaller:
                case UpdateStep.DownloadingUpdate:
                case UpdateStep.UpdateDownloaded:
                    return new ResponseResult(UpdateErrorCode.UpdateAlreadyDownloaded, `Update already downloaded, or download is in progress.`);
                case UpdateStep.InstallingUpdate:
                case UpdateStep.PreparingUpdate:
                    return new ResponseResult(UpdateErrorCode.UpdateAlreadyInstalled, `Update already installed, or installation is in progress.`);
                default:
                    return yield this.DownloadControllerUpdateNewApiAsync(result.Data, updateProgress, downloadProgress, downloadOnly);
            }
        });
    }
    UpdateControllerUsingApiAsync(updateProgress, downloadProgress, downloadOnly) {
        return __awaiter(this, void 0, void 0, function* () {
            if (downloadOnly)
                return yield this.DownloadControllerUpdateUsingApiAsync(updateProgress, downloadProgress, true);
            else
                return yield this.InstallControllerUpdateUsingApiAsync(updateProgress, downloadProgress);
        });
    }
    getRuntimeInfoAsync() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this._updateProviders.GetSoftwareRuntimeInfoAsync();
            }
            catch (error) {
                if (error instanceof CannotGetRuntimeInfo) {
                    if (error.code == CannotGetRuntimeErrorCode.Timeout)
                        return new ResponseResult(UpdateErrorCode.CannotGetCurrentControllerVersionTimeout, `Cannot get runtime info (current controller version), timeout error - please restart controller to fix issue.`);
                }
                return new ResponseResult(UpdateErrorCode.CannotGetCurrentControllerVersion, `Cannot get runtime info (current controller version), error: ${error}`);
            }
        });
    }
}
UpdatesService.ServiceName = "UpdatesService";
class CachedUpdateInfo extends UpdateInfo {
    constructor() {
        super(...arguments);
        this.UpdateIdentifier = "";
        this.ProviderName = "";
        this.UpdateType = null;
    }
}
/**
 * @type Class
 */
export class ControllerUpdateNotAvailableInfo {
    constructor() {
        this.UpdatedResourceName = new Map();
        this.CurrentResourceVersion = "";
        this.UpdateType = null;
        this.ResponseResult = null;
    }
}
class ControllerUpdate {
    constructor() {
        this._updateIdentifier = "";
        this._updateResourceName = new Map();
        this._updateType = null;
        this._currentResourceVersion = "";
        this._newResourceVersion = "";
        this._updateDescription = new Map();
        this._updateIsDownloading = false;
        this._updateCheckDownloadProgress = 0;
        this._updateCreationTime = null;
        this._isDownloadOnly = false;
    }
    get IsDownloadOnly() {
        return this._isDownloadOnly;
    }
    set IsDownloadOnly(value) {
        this._isDownloadOnly = value;
    }
    get UpdateCreationTime() {
        return this._updateCreationTime;
    }
    get DownloadProgress() {
        return this._updateCheckDownloadProgress;
    }
    get UpdateDescription() {
        return this._updateDescription;
    }
    get UpdateIdentifier() {
        return this._updateIdentifier;
    }
    get UpdatedResourceName() {
        return this._updateResourceName;
    }
    get UpdateType() {
        return this._updateType;
    }
    get CurrentResourceVersion() {
        return this._currentResourceVersion;
    }
    get NewResourceVersion() {
        return this._newResourceVersion;
    }
    get UpdateIsDownloading() {
        return this._updateIsDownloading;
    }
    set UpdateCreationTime(value) {
        this._updateCreationTime = value;
    }
    set DownloadProgress(value) {
        this._updateCheckDownloadProgress = value;
    }
    set UpdateDescription(value) {
        this._updateDescription = value;
    }
    set UpdateIdentifier(value) {
        this._updateIdentifier = value;
    }
    set UpdatedResourceName(value) {
        this._updateResourceName = value;
    }
    set UpdateType(value) {
        this._updateType = value;
    }
    set CurrentResourceVersion(value) {
        this._currentResourceVersion = value;
    }
    set NewResourceVersion(value) {
        this._newResourceVersion = value;
    }
    set UpdateIsDownloading(value) {
        this._updateIsDownloading = value;
    }
}
class DeviceUpdate {
    constructor() {
        this._updatedResourceName = "";
        this._updateResourceGuid = "";
        this._protocol = "";
        this._protocolGuid = "";
        this._currentResourceVersion = "";
        this._newResourceVersion = "";
        this._isUpdateSafe = true;
        this._updateDescription = new Map();
    }
    get UpdateDescription() {
        return this._updateDescription;
    }
    set UpdateDescription(value) {
        this._updateDescription = value;
    }
    get UpdatedResourceName() {
        return this._updatedResourceName;
    }
    get UpdateResourceGuid() {
        return this._updateResourceGuid;
    }
    get Protocol() {
        return this._protocol;
    }
    get ProtocolGuid() {
        return this._protocolGuid;
    }
    get CurrentResourceVersion() {
        return this._currentResourceVersion;
    }
    get NewResourceVersion() {
        return this._newResourceVersion;
    }
    get IsUpdateSafe() {
        return this._isUpdateSafe;
    }
    set UpdatedResourceName(value) {
        this._updatedResourceName = value;
    }
    set UpdateResourceGuid(value) {
        this._updateResourceGuid = value;
    }
    set Protocol(value) {
        this._protocol = value;
    }
    set ProtocolGuid(value) {
        this._protocolGuid = value;
    }
    set CurrentResourceVersion(value) {
        this._currentResourceVersion = value;
    }
    set NewResourceVersion(value) {
        this._newResourceVersion = value;
    }
    set IsUpdateSafe(value) {
        this._isUpdateSafe = value;
    }
}
//Install update
class ScriptControllerUpdateRequest extends DataFrame {
    constructor(uri) {
        super();
        this.Resource = "/updates/update/from/uri";
        this.Method = Method.Post;
        this.Data = uri;
    }
}
class UpdateStatusFrame {
    constructor() {
        this.ExpectedInstallationTime = 0;
        this.Guid = "";
        this.Package = new JsonPackageSchema();
        this.Progress = 0;
        this.State = UpdateState.PreparingSystem;
        this.UserInput = UserInput.Install;
    }
}
class JsonPackageSchema {
    constructor() {
        this.Package = new JsonPackage();
        this.Publisher = new Publisher();
    }
}
class JsonPackage {
    constructor() {
        this.CompatibleHardwareVersions = [];
        this.CompatibleOperatingSystems = [];
        this.CompatibleProcessorArch = [];
        this.CompatibleSoftwareVersions = [];
        this.DependencyRequiredExtensions = [];
        this.Description = new Map();
        this.Guid = "";
        this.Name = new Map();
        this.PackageAPIVersion = 0;
        this.PackageType = PackageType.Unknown;
        this.PackageVersion = 0;
        this.PublicationTime = Date.now().toString();
        this.PublisherGuid = "";
        this.RequiredDownloadDiskSpace = 0;
        this.RequiredInstallDiskSpace = 0;
        this.UpdateChannels = [];
    }
}
class Hardware {
    constructor() {
        this.Guid = "";
        this.MaxVersion = 0;
        this.MinVersion = 0;
    }
}
var OperatingSystem;
(function (OperatingSystem) {
    OperatingSystem[OperatingSystem["Windows"] = 0] = "Windows";
    OperatingSystem[OperatingSystem["Linux"] = 1] = "Linux";
    OperatingSystem[OperatingSystem["Unix"] = 2] = "Unix";
    OperatingSystem[OperatingSystem["OSX"] = 3] = "OSX";
    OperatingSystem[OperatingSystem["Android"] = 4] = "Android";
    OperatingSystem[OperatingSystem["iOS"] = 5] = "iOS";
    OperatingSystem[OperatingSystem["WindowsMobile"] = 6] = "WindowsMobile";
})(OperatingSystem || (OperatingSystem = {}));
var ProcessorArch;
(function (ProcessorArch) {
    ProcessorArch[ProcessorArch["Any"] = 0] = "Any";
    ProcessorArch[ProcessorArch["MIPS"] = 1] = "MIPS";
    ProcessorArch[ProcessorArch["ARM32"] = 2] = "ARM32";
    ProcessorArch[ProcessorArch["ARM64"] = 3] = "ARM64";
    ProcessorArch[ProcessorArch["IntelX86"] = 4] = "IntelX86";
    ProcessorArch[ProcessorArch["IntelX64"] = 5] = "IntelX64";
})(ProcessorArch || (ProcessorArch = {}));
class DependencyExtension {
    constructor() {
        this.CompatibleVersions = 0;
        this.ExtensionGuid = 0;
        this.MaximumCompatibleVersion = 0;
        this.MinimumCompatibleVersion = 0;
    }
}
var PackageType;
(function (PackageType) {
    PackageType[PackageType["Unknown"] = 0] = "Unknown";
    PackageType[PackageType["Environment"] = 1] = "Environment";
    PackageType[PackageType["OperatingSystem"] = 2] = "OperatingSystem";
    PackageType[PackageType["Application"] = 3] = "Application";
    PackageType[PackageType["Extension"] = 4] = "Extension";
    PackageType[PackageType["HardwareFirmware"] = 5] = "HardwareFirmware";
    PackageType[PackageType["Script"] = 6] = "Script";
    PackageType[PackageType["Command"] = 7] = "Command";
})(PackageType || (PackageType = {}));
class Publisher {
    constructor() {
        this.Description = "";
        this.Email = "";
        this.Guid = "";
        this.Name = "";
        this.Phone = "";
        this.PublisherGuid = "";
        this.Webpage = "";
    }
}
class PendingUpdateApiObject {
    constructor() {
        this.Id = "";
        this.Update = new UpdateApiObject();
        this.UpdateDownloadPath = "";
        this.InstallerDownloadPath = "";
        this.DownloadDateTime = "";
        this.UpdateStep = UpdateStep.Unknown;
        this.StepProgressInPercentage = 0;
        this.UpdateProgressInPercentage = 0;
        this.DownloadSpeed = 0;
        this.DownloadProgress = 0;
        this.DownloadOnly = false;
        this.RawUpdateJsonFile = "";
    }
}
class UpdateApiObject {
    constructor() {
        this.Guid = "";
        this.Creationtime = "";
        this.Version = new UpdateVersion();
        this.UpdateChannel = "";
        this.UpdateFileHash = "";
        this.UpdateFileUrl = "";
        this.VerifySign = "";
        this.InstallerFileUrl = "";
        this.InstallerFileHash = "";
        this.InstallerVerifySign = "";
    }
}
class DeviceUpdateInfoApiObject {
    constructor() {
        this.DeviceGuid = "";
        this.IsAvailable = false;
        this.NewVersion = "";
        this.CurrentVersion = "";
    }
}
class UpdateVersion {
    constructor() {
        this.BaseRuntime = 0;
        this.ContainerSoftwareVersion = 0;
    }
}
var UpdateStep;
(function (UpdateStep) {
    UpdateStep[UpdateStep["InstallerFileSignCheckFailed"] = -5] = "InstallerFileSignCheckFailed";
    UpdateStep[UpdateStep["UpdateFileSignCheckFailed"] = -4] = "UpdateFileSignCheckFailed";
    UpdateStep[UpdateStep["UpdateInstallationFailed"] = -3] = "UpdateInstallationFailed";
    UpdateStep[UpdateStep["UpdateDownloadFailed"] = -2] = "UpdateDownloadFailed";
    UpdateStep[UpdateStep["UpdateFileCheckFailed"] = -1] = "UpdateFileCheckFailed";
    UpdateStep[UpdateStep["Unknown"] = 0] = "Unknown";
    UpdateStep[UpdateStep["DownloadingUpdate"] = 1] = "DownloadingUpdate";
    UpdateStep[UpdateStep["CheckingUpdate"] = 2] = "CheckingUpdate";
    UpdateStep[UpdateStep["PreparingUpdate"] = 3] = "PreparingUpdate";
    UpdateStep[UpdateStep["InstallingUpdate"] = 4] = "InstallingUpdate";
    UpdateStep[UpdateStep["DownloadingInstaller"] = 5] = "DownloadingInstaller";
    UpdateStep[UpdateStep["UpdateDownloaded"] = 6] = "UpdateDownloaded";
    UpdateStep[UpdateStep["OfflineUpdateStep"] = 7] = "OfflineUpdateStep";
})(UpdateStep || (UpdateStep = {}));
class UpdateProgressResponse {
    constructor() {
        this.UpdateStep = UpdateStep.Unknown;
        this.StepProgressInPercentage = 0;
        this.UpdateProgressInPercentage = 0;
        this.DownloadSpeedInMbs = 0;
    }
}
class InstallRequest extends DataFrame {
    constructor(uri) {
        super();
        this.Resource = "/updates/install/from/url";
        this.Method = Method.Put;
        this.Data = uri;
    }
}
class LoginUserRequest extends DataFrame {
    constructor(data) {
        super();
        this.Resource = "/users/user/login";
        this.Method = Method.Put;
        this.Data = data;
    }
}
class LoginUser {
    constructor() {
        this.Email = "";
        this.Password = "";
    }
}
class ExecuteCommandRequest extends DataFrame {
    constructor(command) {
        super();
        this.Resource = "/controller/runtime/execute";
        this.Method = Method.Post;
        this.Data = command;
    }
}
class GetUpdateBranchRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/branch";
        this.Method = Method.Get;
    }
}
class SetUpdateBranchRequest extends DataFrame {
    constructor(data) {
        super();
        this.Resource = "/updates/branch";
        this.Method = Method.Put;
        this.Data = data;
    }
}
class CancelFailedUpdateRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/failed/cancel";
        this.Method = Method.Put;
    }
}
class PendingUpdateProgressRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/pending/progress";
        this.Method = Method.Get;
    }
}
class PendingUpdatesRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/pending";
        this.Method = Method.Get;
    }
}
class CheckUpdatesRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/check";
        this.Method = Method.Get;
    }
}
class DownloadUpdateRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/download";
        this.Method = Method.Post;
    }
}
class InstallUpdateRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/install";
        this.Method = Method.Post;
    }
}
class GetAutomaticUpdatesStatusRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/download/automatic/status";
        this.Method = Method.Get;
    }
}
class EnableAutomaticUpdatesRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/download/automatic/enable";
        this.Method = Method.Put;
    }
}
class DisableAutomaticUpdatesRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/download/automatic/disable";
        this.Method = Method.Put;
    }
}
class GetUpdatesHistoryRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/history";
        this.Method = Method.Get;
    }
}
class CheckDevicesUpdatesRequest extends DataFrame {
    constructor() {
        super();
        this.Resource = "/updates/devices/availability";
        this.Method = Method.Get;
    }
}
//# sourceMappingURL=UpdatesService.js.map